import React from "react";
import {
  FiArrowRight,
  FiBookOpen,
  FiCheckCircle,
  FiColumns,
  FiCreditCard,
  FiCpu,
  FiGlobe,
  FiHeart,
  FiHome,
  FiMonitor,
  FiSearch,
  FiSend,
  FiShield,
  FiTarget,
  FiUserCheck,
  FiZap,
} from "react-icons/fi";
import "./Industries.css";

type Accent = "cyan" | "blue" | "violet" | "orange" | "indigo" | "teal" | "navy" | "green";

interface Industry {
  id: string;
  title: string;
  navTitle: string;
  description: string;
  icon: React.ReactNode;
  accent: Accent;
  outcomes: string[];
}

const orbitOneUrl = "https://orbitone.live";

const industries: Industry[] = [
  {
    id: "startups",
    title: "Startups",
    navTitle: "Startups",
    description: "Move from idea to setup, launch and early growth with guided workflows.",
    icon: <FiSend />,
    accent: "cyan",
    outcomes: ["Incorporation support", "Digital presence", "Lead capture", "AI-assisted operations"],
  },
  {
    id: "msmes",
    title: "MSMEs",
    navTitle: "MSMEs",
    description: "Build digital presence, manage leads, access services and enable payment-ready operations.",
    icon: <FiHome />,
    accent: "blue",
    outcomes: ["Website and SEO setup", "Service requests", "Payments", "Growth workspace"],
  },
  {
    id: "manufacturing",
    title: "Manufacturing",
    navTitle: "Manufacturing",
    description: "Connect compliance, procurement, documentation and operational support in one workspace.",
    icon: <FiCpu />,
    accent: "violet",
    outcomes: ["Vendor inquiries", "Compliance workflows", "Service tracking", "Growth opportunities"],
  },
  {
    id: "education",
    title: "Education",
    navTitle: "Education",
    description: "Support student ventures, campus entrepreneurship and structured institutional programs.",
    icon: <FiBookOpen />,
    accent: "indigo",
    outcomes: ["Student onboarding", "Venture tracking", "Mentorship requests", "Opportunity discovery"],
  },
  {
    id: "healthcare",
    title: "Healthcare",
    navTitle: "Healthcare",
    description: "Create structured, compliant digital journeys for services, requests and operations.",
    icon: <FiHeart />,
    accent: "teal",
    outcomes: ["Service workflows", "Request management", "Compliance support", "Digital presence"],
  },
  {
    id: "service",
    title: "Service",
    navTitle: "Service",
    description: "Enable structured onboarding, applications, documentation and service delivery workflows.",
    icon: <FiColumns />,
    accent: "navy",
    outcomes: ["Application flows", "Document tracking", "Citizen / business support", "Program management"],
  },
];

const executionLayer = [
  {
    title: "Onboarding",
    description: "Guided setup from day one",
    icon: <FiTarget />,
    accent: "cyan",
  },
  {
    title: "Digital Presence",
    description: "Web, SEO and brand visibility",
    icon: <FiMonitor />,
    accent: "blue",
  },
  {
    title: "Opportunity Discovery",
    description: "Leads, grants and opportunities",
    icon: <FiSearch />,
    accent: "teal",
  },
  {
    title: "Payments",
    description: "Collect, manage and reconcile",
    icon: <FiCreditCard />,
    accent: "orange",
  },
  {
    title: "Managed Services",
    description: "Raise requests and get expert help",
    icon: <FiUserCheck />,
    accent: "cyan",
  },
  {
    title: "AI Workflows",
    description: "Automate tasks and scale operations",
    icon: <FiZap />,
    accent: "green",
  },
];

const heroTiles = industries.slice(0, 8);

export const Industries: React.FC = () => {
  return (
    <main className="industries-page">
      <section className="industries-hero-shell" id="industries-hero">
        <div className="industries-hero-copy">
          <span className="industries-kicker">Industries</span>
          <h1>Built for every business journey</h1>
          <p>
            Stellar supports founders, MSMEs, institutions and sector-focused teams
            with connected workflows for onboarding, digital presence, opportunity
            discovery, payments, services and AI-assisted operations.
          </p>
          <div className="industries-hero-actions">
            <a href="#industry-solutions" className="industries-button industries-button-primary">
              Explore industries
              <FiArrowRight />
            </a>
            <a href={orbitOneUrl} className="industries-button industries-button-secondary">
              Talk to us
              <FiArrowRight />
            </a>
          </div>
        </div>

        <div className="industries-hero-grid" aria-label="Featured industries">
          {heroTiles.map((industry) => (
            <a
              key={industry.id}
              href={`#${industry.id}`}
              className="industries-hero-tile"
              data-accent={industry.accent}
            >
              <span className="industries-hero-icon">{industry.icon}</span>
              <strong>{industry.title}</strong>
            </a>
          ))}
        </div>
      </section>

      <section className="industries-strip" aria-labelledby="industries-strip-title">
        <h2 id="industries-strip-title">Industries we serve</h2>
        <div className="industries-strip-row">
          {industries.map((industry) => (
            <a key={industry.id} href={`#${industry.id}`} className="industry-pill">
              <span data-accent={industry.accent}>{industry.icon}</span>
              {industry.navTitle}
            </a>
          ))}
        </div>
      </section>

      <section className="industry-solutions" id="industry-solutions">
        <div className="industries-section-heading">
          <h2>Solutions for modern sectors</h2>
          <p>
            Each industry has different needs, but the execution layer stays connected:
            setup, presence, discovery, payments, requests and AI workflows.
          </p>
        </div>

        <div className="industry-card-grid">
          {industries.map((industry) => (
            <article
              key={industry.id}
              id={industry.id}
              className="industry-solution-card"
              data-accent={industry.accent}
            >
              <span className="industry-card-icon">{industry.icon}</span>
              <div>
                <h3>{industry.title}</h3>
                <p>{industry.description}</p>
              </div>
              <ul>
                {industry.outcomes.map((outcome) => (
                  <li key={outcome}>
                    <FiCheckCircle />
                    <span>{outcome}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="execution-layer" id="execution-layer">
        <div className="industries-section-heading">
          <h2>One execution layer across industries</h2>
          <p>Stellar connects the core workflow pieces every sector needs.</p>
        </div>
        <div className="execution-grid">
          {executionLayer.map((item) => (
            <article key={item.title} className="execution-card" data-accent={item.accent}>
              <span>{item.icon}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="industries-cta-panel">
        <div>
          <h2>Ready to build your sector workflow?</h2>
          <p>
            Use Stellar to move from intent to execution with a platform designed
            for practical business journeys.
          </p>
        </div>
        <div className="industries-cta-actions">
          <a href={orbitOneUrl} className="industries-button industries-button-primary">
            Start your journey
            <FiArrowRight />
          </a>
          <a href="/products" className="industries-button industries-button-secondary">
            Explore products
            <FiArrowRight />
          </a>
        </div>
      </section>

      <a href={orbitOneUrl} className="industries-chat-float" aria-label="Contact Stellar">
        <FiGlobe />
      </a>
    </main>
  );
};

export default Industries;
